import React, { useState } from 'react';
import {
  Text,
  Icon,
  useDisclosure,
  useToast,
  Box,
  Flex,
  SimpleGrid,
  Spinner
} from '@chakra-ui/react';
import {
  FiFileText,
  FiCalendar,
  FiActivity,
  FiLogOut
} from 'react-icons/fi';

// Components
import Card from 'components/Card/Card.js';

// Modales (los crearemos después)
import FormSelectorModal from './modals/FormSelectorModal';
import NewAppointmentModal from 'views/Dashboard/Agenda/components/NewAppointmentModal';
import NewSickExitModal from './modals/NewSickExitModal';
import NewPedagogicalExitModal from './modals/NewPedagogicalExitModal';

// Utils
import { useUserAuth, useRoleModuleAccess, MODULES } from 'utils/site';

const QuickActionButton = ({
  icon,
  title,
  description,
  palette = 'sky',
  isLoading = false,
  onClick
}) => {
  const bg = palette === 'coral' ? 'coral.soft' : `${palette}.bg`;
  const ink = palette === 'coral' ? 'coral.700' : `${palette}.ink`;

  return (
    <Flex
      direction="column"
      gap="9px"
      align="flex-start"
      borderRadius="14px"
      p="14px"
      bg={bg}
      cursor="pointer"
      border="1.5px solid transparent"
      transition="0.15s"
      onClick={onClick}
      _hover={{ transform: 'translateY(-2px)', borderColor: 'rgba(0,0,0,.07)' }}
    >
      <Flex w="38px" h="38px" borderRadius="11px" bg="white" align="center" justify="center" color={ink} flexShrink={0}>
        {isLoading ? <Spinner size="sm" /> : <Icon as={icon} boxSize="19px" />}
      </Flex>
      <Box>
        <Text fontWeight="700" fontSize="13.5px" lineHeight="1.2" color={ink}>
          {title}
        </Text>
        <Text fontSize="11.5px" fontWeight="500" opacity={0.75} mt="1px" color={ink}>
          {description}
        </Text>
      </Box>
    </Flex>
  );
};

const QuickActions = ({ onActionComplete }) => {
  // Obtener información del usuario y permisos
  const { user } = useUserAuth();
  const hasSistemaAccess = useRoleModuleAccess(MODULES.SISTEMA);
  const hasAccessControlAccess = useRoleModuleAccess(MODULES.ACCESS_CONTROL);

  // Estados de los modales
  const { isOpen: isFormSelectorOpen, onOpen: onFormSelectorOpen, onClose: onFormSelectorClose } = useDisclosure();
  const { isOpen: isNewCitaOpen, onOpen: onNewCitaOpen, onClose: onNewCitaClose } = useDisclosure();
  const { isOpen: isSickExitOpen, onOpen: onSickExitOpen, onClose: onSickExitClose } = useDisclosure();
  const { isOpen: isPedagogicalExitOpen, onOpen: onPedagogicalExitOpen, onClose: onPedagogicalExitClose } = useDisclosure();

  // Estados de loading
  const [loadingStates, setLoadingStates] = useState({
    form: false,
    event: false,
    batch: false
  });

  const toast = useToast();

  // Handler para acciones completadas
  const handleActionComplete = (actionType, success = true, message = '') => {
    if (success) {
      toast({
        title: "Acción completada",
        description: message || `${actionType} realizada exitosamente`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right"
      });
      
      // Notificar al componente padre para refrescar datos
      onActionComplete && onActionComplete();
    } else {
      toast({
        title: "Error en la acción",
        description: message || `Error al realizar ${actionType}`,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right"
      });
    }
    
    // Reset loading state
    setLoadingStates(prev => ({
      ...prev,
      [actionType]: false
    }));
  };

  // Handler para generación de cobros masivos
  const handleBatchPayments = async () => {
    setLoadingStates(prev => ({ ...prev, batch: true }));
    
    try {
      // Aquí iría la lógica para abrir el modal de cobros masivos
      // Por ahora simulamos la acción
      console.log('🔄 Iniciando generación de cobros masivos...');
      
      setTimeout(() => {
        handleActionComplete('batch', true, 'Proceso de cobros masivos iniciado');
      }, 1000);
      
    } catch (error) {
      handleActionComplete('batch', false, error.message);
    }
  };

  // Configurar acciones según permisos de rol
  const actions = [
    // Llenar Formulario - Todos los roles (Primera posición)
    {
      icon: FiFileText,
      title: 'Ficha de atención',
      description: 'Seleccionar y completar formulario',
      palette: 'lav',
      onClick: () => {
        setLoadingStates(prev => ({ ...prev, form: true }));
        onFormSelectorOpen();
      },
      loading: loadingStates.form
    },
    // Nueva Cita - Todos los roles con acceso a agenda
    {
      icon: FiCalendar,
      title: 'Agendar nueva cita',
      description: 'Agendar cita con un alumno',
      palette: 'sky',
      onClick: () => {
        setLoadingStates(prev => ({ ...prev, event: true }));
        onNewCitaOpen();
      },
      loading: loadingStates.event
    },
    // Salida por enfermedad — requiere acceso a Sistema (solo administradores)
    ...(hasSistemaAccess ? [
      {
        icon: FiActivity,
        title: 'Salida por enfermedad',
        description: 'Registrar salida de un alumno',
        palette: 'coral',
        onClick: onSickExitOpen
      }
    ] : []),
    // Salida pedagógica — mismo permiso que habilita "Accesos QR" en el menú
    // lateral (administradores y profesores)
    ...(hasAccessControlAccess ? [
      {
        icon: FiLogOut,
        title: 'Salida pedagógica',
        description: 'Programar salida de un curso',
        palette: 'lav',
        onClick: onPedagogicalExitOpen
      }
    ] : [])
  ];

  return (
    <>
      <Card p={0} borderRadius="22px" overflow="hidden">
        <Box p="18px 22px 6px">
          <Text fontSize="16px" fontWeight="800" letterSpacing="-0.01em">
            Acciones rápidas
          </Text>
        </Box>

        <Box p="14px 16px 18px">
          {actions.length > 0 ? (
            <SimpleGrid columns={2} spacing="10px">
              {actions.map((action) => (
                <QuickActionButton
                  key={action.title}
                  icon={action.icon}
                  title={action.title}
                  description={action.description}
                  palette={action.palette}
                  isLoading={action.loading}
                  onClick={action.onClick}
                />
              ))}
            </SimpleGrid>
          ) : (
            <Text fontSize="sm" color="ink.400" textAlign="center" py={4}>
              No hay acciones disponibles para tu rol
            </Text>
          )}
        </Box>
      </Card>

      {/* Modales */}
      <FormSelectorModal
        isOpen={isFormSelectorOpen}
        onClose={() => {
          onFormSelectorClose();
          setLoadingStates(prev => ({ ...prev, form: false }));
        }}
        onSuccess={(message) => handleActionComplete('form', true, message)}
        onError={(message) => handleActionComplete('form', false, message)}
      />

      <NewAppointmentModal
        isOpen={isNewCitaOpen}
        onClose={() => {
          onNewCitaClose();
          setLoadingStates(prev => ({ ...prev, event: false }));
        }}
        teacherId={user?.id}
      />

      <NewSickExitModal
        isOpen={isSickExitOpen}
        onClose={onSickExitClose}
        onSuccess={(message) => handleActionComplete('Salida por enfermedad', true, message)}
        onError={(message) => handleActionComplete('Salida por enfermedad', false, message)}
      />

      <NewPedagogicalExitModal
        isOpen={isPedagogicalExitOpen}
        onClose={onPedagogicalExitClose}
        onSuccess={(message) => handleActionComplete('Salida pedagógica', true, message)}
        onError={(message) => handleActionComplete('Salida pedagógica', false, message)}
      />
    </>
  );
};

export default QuickActions;